import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import { useNavigate } from "react-router-dom";
import type { PlayerAIClientAction } from "@playbookai/api-clients";
import type {
  PlatformModuleId,
  PrivatePlayerAIEntityAttachment,
  PrivatePlayerAIHandoff,
} from "@playbookai/types";
import type { InteractionContext } from "@playbookai/player-ai-client";
import { PlayerAIRailWired } from "../player-ai/PlayerAIRailWired.js";
import { useAuthStore } from "../auth/auth-store.js";
import { getEnabledModules, isModuleAvailable } from "../modules/index.js";
import {
  isPlatformNavigationAction,
  resolvePlatformNavigation,
} from "../player-ai/navigation.js";
import { receivePrivatePlayerAIHandoff } from "../player-ai/private-handoff.js";
import { useTabs } from "../store/tabs.js";

/** Standalone same-origin destination used only after an explicit new-tab action. */
export function PlayerAIPage(): ReactElement {
  const navigate = useNavigate();
  const enabledModuleKeys = useAuthStore(
    (state) => state.claims?.enabledModules,
  );
  const enabledModules = useMemo(
    () => getEnabledModules(enabledModuleKeys),
    [enabledModuleKeys],
  );
  const [handoff, setHandoff] = useState<PrivatePlayerAIHandoff | null>(null);
  const [entity, setEntity] = useState<
    PrivatePlayerAIEntityAttachment | undefined
  >(undefined);
  const handoffRequestRef = useRef<
    Promise<PrivatePlayerAIHandoff | null> | undefined
  >(undefined);

  const isAvailable = useCallback(
    (module: PlatformModuleId) => isModuleAvailable(module, enabledModuleKeys),
    [enabledModuleKeys],
  );

  useEffect(() => {
    let mounted = true;
    handoffRequestRef.current ??= receivePrivatePlayerAIHandoff();
    void handoffRequestRef.current.then((received) => {
      if (!mounted || !received) return;
      setHandoff(received);
      setEntity(received.entity);
    });
    return () => {
      mounted = false;
    };
  }, []);

  // ADR-0100: a record of an app that is off is not attached, even if it was
  // handed off before the app went off; the composer text still arrives.
  const attachedEntity =
    entity && isAvailable(entity.ref.module) ? entity : undefined;

  const interactionContext = useMemo<InteractionContext>(
    () => ({
      current_page_context: {
        surface: "player_ai",
        ...(attachedEntity ? { selected_refs: [attachedEntity.ref] } : {}),
      },
    }),
    [attachedEntity],
  );
  const onClientAction = useCallback(
    (action: PlayerAIClientAction): boolean => {
      if (!isPlatformNavigationAction(action)) return false;
      const destination = resolvePlatformNavigation(
        action,
        enabledModules,
        (moduleId) => String(useTabs.getState().nextCount(moduleId)),
        isAvailable,
      );
      if (!destination) return false;
      navigate(destination.route);
      return true;
    },
    [enabledModules, isAvailable, navigate],
  );

  return (
    <main
      aria-label="Player AI"
      className="pb-motion-content-enter min-h-screen bg-pb-shell text-pb-text"
    >
      <PlayerAIRailWired
        scope={{ conversationType: "private" }}
        context={{ label: "Player AI" }}
        variant="expanded"
        initialTab="chat"
        mode="fullscreen"
        initialFocus="composer"
        interactionContext={interactionContext}
        onClientAction={onClientAction}
        {...(handoff?.composer_text.trim()
          ? {
              draftRequest: {
                id: handoff.handoff_id,
                text: handoff.composer_text,
              },
            }
          : {})}
        {...(attachedEntity
          ? {
              draftEntityAttachment: attachedEntity,
              onRemoveDraftEntityAttachment: () => setEntity(undefined),
            }
          : {})}
        onToggle={() => window.close()}
      />
    </main>
  );
}
